import type {
  EffectiveOpeningDayResponse,
  EffectiveOpeningHoursResponse
} from 'generated/index';

import { DAY_TYPES, WEEKDAYS } from '../OpeningHours.constants';
import type { Weekday } from '../OpeningHours.types';
import { toDateString } from 'utils/date';

const WEEKDAY_BY_DAY_OF_WEEK: Record<
  EffectiveOpeningDayResponse['dayOfWeek'],
  Weekday
> = {
  MONDAY: 'monday',
  TUESDAY: 'tuesday',
  WEDNESDAY: 'wednesday',
  THURSDAY: 'thursday',
  FRIDAY: 'friday',
  SATURDAY: 'saturday',
  SUNDAY: 'sunday'
};

export const toWeekday = (day: EffectiveOpeningDayResponse): Weekday =>
  WEEKDAY_BY_DAY_OF_WEEK[day.dayOfWeek];

/** Per day type, the window that covers the date ({@code to} exclusive); day types without one are left out. */
export const getWeeksOnDate = (
  weeks: EffectiveOpeningHoursResponse[],
  date: Date | null
): EffectiveOpeningHoursResponse[] => {
  if (!date) return [];
  const day: string = toDateString(date);
  return DAY_TYPES.map((dayType) =>
    weeks.find(
      (week) => week.dayType === dayType && week.from <= day && day < week.to
    )
  ).filter((week): week is EffectiveOpeningHoursResponse => !!week);
};

/**
 * A day type for which neither the plan unit nor any of its parents records opening hours on any
 * weekday: the plan unit is closed on those days.
 */
export const hasNoOpeningHours = (week: EffectiveOpeningHoursResponse) =>
  week.days.every((day) => day.source === 'NONE');

/** The weekdays of a week in the order the overview shows them. */
export const sortDays = (days: EffectiveOpeningDayResponse[]) =>
  [...days].sort(
    (a, b) => WEEKDAYS.indexOf(toWeekday(a)) - WEEKDAYS.indexOf(toWeekday(b))
  );
